/**
 * Event instrumentation — the always-on analytics layer.
 *
 * The renderer emits a typed event set; the bus fans each event out to every
 * configured sink (GA4, Microsoft Clarity, Meta Pixel). It also pushes a
 * virtual URL per step so GA4 pageviews and Clarity heatmaps segment by
 * question. A funnel config cannot disable this layer. PLAN.md §8.6.
 */
import type { AnalyticsConfig, FunnelSettings } from "../types";
import type { D1Sink } from "./d1Sink";

export type FunnelEventName =
  | "funnel_start"
  | "step_view"
  | "field_change"
  | "field_error"
  | "step_complete"
  | "value_card_view"
  | "branch_taken"
  | "variant_assignment"
  | "funnel_partial"
  | "funnel_submit"
  | "funnel_abandon"
  // ── W1-C2 ADDITIVE events 2026-05-24 (per ADDITIVE EVENT TAXONOMY rule —
  //    no renames; only new event names added):
  | "form_submit_attempt"        // fires when Submit clicked, BEFORE validation/server call
  | "form_submit_failure"        // fires on validation failure OR Worker non-200 response
  | "back_navigation"            // fires when user clicks Back on any step
  | "outcome_view"               // fires when result page renders (success or DQ)
  | "funnel_dq_early";           // fires when DQ rules match before contact step (Option A early-exit)

export interface EventProps {
  step_id?: string;
  step_index?: number;
  field_id?: string;
  /** Extra event-specific dimensions. */
  [k: string]: string | number | boolean | undefined;
}

/* global window, document */
declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
    clarity?: (...args: unknown[]) => void;
    fbq?: (...args: unknown[]) => void;
    _fbq?: unknown;
  }
}

function injectScript(src: string, async = true): void {
  const s = document.createElement("script");
  s.src = src;
  s.async = async;
  document.head.appendChild(s);
}

export class EventBus {
  private readonly funnelId: string;
  private readonly sessionId: string;
  private readonly analytics: AnalyticsConfig;
  private readonly settings: FunnelSettings;
  private readonly d1Sink: D1Sink | null;
  private started = false;

  constructor(
    funnelId: string,
    sessionId: string,
    analytics: AnalyticsConfig,
    settings: FunnelSettings,
    d1Sink: D1Sink | null = null,
  ) {
    this.funnelId = funnelId;
    this.sessionId = sessionId;
    this.analytics = analytics;
    this.settings = settings;
    this.d1Sink = d1Sink;
  }

  /** Inject GA4 / Clarity / Pixel snippets. Idempotent. */
  init(): void {
    if (this.started) return;
    this.started = true;
    this.initGa4();
    this.initClarity();
    this.initPixel();
  }

  private initGa4(): void {
    const id = this.analytics.ga4_measurement_id;
    if (!id) return;
    window.dataLayer = window.dataLayer ?? [];
    window.gtag = function gtag() {
      // eslint-disable-next-line prefer-rest-params
      window.dataLayer!.push(arguments);
    };
    window.gtag("js", new Date());
    // We emit our own page_view per virtual step — disable the automatic one.
    window.gtag("config", id, { send_page_view: false });
    injectScript(`https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(id)}`);
  }

  private initClarity(): void {
    const id = this.analytics.clarity_project_id;
    if (!id || window.clarity) return;
    const c = function clarity() {
      (c as unknown as { q: unknown[] }).q =
        (c as unknown as { q?: unknown[] }).q ?? [];
      // eslint-disable-next-line prefer-rest-params
      (c as unknown as { q: unknown[] }).q.push(arguments);
    };
    window.clarity = c as unknown as Window["clarity"];
    injectScript(`https://www.clarity.ms/tag/${encodeURIComponent(id)}`);
  }

  private initPixel(): void {
    const id = this.analytics.meta_pixel_id;
    if (!id) return;
    if (!window.fbq) {
      const fbq = function fbq() {
        const q = fbq as unknown as { callMethod?: Function; queue: unknown[] };
        // eslint-disable-next-line prefer-rest-params
        if (q.callMethod) q.callMethod.apply(fbq, arguments as unknown as unknown[]);
        else q.queue.push(arguments);
      };
      (fbq as unknown as { queue: unknown[] }).queue = [];
      window.fbq = fbq as unknown as Window["fbq"];
      window._fbq = window._fbq ?? fbq;
      injectScript("https://connect.facebook.net/en_US/fbevents.js");
    }
    window.fbq!("init", id);
    window.fbq!("track", "PageView");
  }

  /** Emit one funnel event to GA4 + Clarity + (W1-C1) D1 funnel_events. */
  emit(name: FunnelEventName, props: EventProps = {}): void {
    const payload = {
      funnel_id: this.funnelId,
      session_id: this.sessionId,
      ...props,
    };
    window.gtag?.("event", name, payload);
    // Clarity: tag the recording so sessions are filterable by funnel event.
    window.clarity?.("event", name);
    if (props.step_id) window.clarity?.("set", "funnel_step", String(props.step_id));

    // W1-C1 D1 funnel_events fan-out: buffered + flushed via sendBeacon to
    // /api/funnel_events by D1Sink. Optional dependency — null d1Sink means
    // D1 tracking disabled (e.g. dev/preview without Worker endpoint configured).
    this.d1Sink?.push(name, props);
  }

  /** Fire a Meta Pixel event (standard or custom) at a checkpoint.
   *
   * When `eventID` is provided, Meta uses it to deduplicate the browser pixel
   * event against a matching server-side CAPI event with the same id — counts
   * the pair as one event in Events Manager. Required for Lead (we fire both
   * browser + CAPI per Stage A). Format must match the Worker's CAPI event_id
   * exactly: `{correlation_id}_{event_name_lowercase}`. See
   * app/lead_distribution/internal_api/src/meta_capi.ts:183. */
  pixel(event: string, params?: Record<string, unknown>, eventID?: string): void {
    if (!this.analytics.meta_pixel_id || !window.fbq) return;
    // Meta standard events — fire via track(); anything else via trackCustom().
    // Standard events get Meta's built-in optimization presets (CPL, CPM, ROAS
    // bidding) and richer reporting; custom events are trackable but not preset.
    const standard = new Set([
      "AddPaymentInfo",
      "AddToCart",
      "AddToWishlist",
      "CompleteRegistration",
      "Contact",
      "CustomizeProduct",
      "Donate",
      "FindLocation",
      "InitiateCheckout",
      "Lead",
      "Purchase",
      "Schedule",
      "Search",
      "StartTrial",
      "SubmitApplication",
      "Subscribe",
      "ViewContent",
    ]);
    const method = standard.has(event) ? "track" : "trackCustom";
    if (eventID) {
      window.fbq(method, event, params ?? {}, { eventID });
    } else {
      window.fbq(method, event, params ?? {});
    }
  }

  /** Push a virtual URL for a step so GA4 + Clarity segment per question. */
  virtualPage(stepId: string, replace: boolean = false): void {
    if (!this.settings.virtual_urls) return;
    const prefix = this.settings.url_path_prefix ?? "/step";
    const path = `${prefix}/${stepId}`;
    try {
      // Forward navigation: pushState so each step adds a browser-history entry,
      // and the browser back button can walk backward through the funnel.
      // Reverse navigation (popstate-driven): replaceState so the post-popstate
      // sync doesn't duplicate the entry the browser just popped to.
      if (replace) {
        window.history.replaceState({ funnelStep: stepId }, "", path);
      } else {
        window.history.pushState({ funnelStep: stepId }, "", path);
      }
    } catch {
      // history API can throw in sandboxed contexts — analytics still emits below.
    }
    window.gtag?.("event", "page_view", {
      page_path: path,
      page_title: `${this.funnelId} — ${stepId}`,
    });
  }
}
